// Lightweight "new order" chime built with the Web Audio API so we don't
// have to ship an mp3 asset and so playback is not throttled by the browser
// waiting on a network fetch. A short two-tone bell (E5 → C5) with a soft
// exponential decay — audible on a busy concession stand without being
// jarring in a quiet moment.

let ctx = null;
let unlocked = false;

// Resume/create the AudioContext. Browsers require a user gesture before
// audio can play, so we call this once from the first click/tap.
export function unlockChime() {
  try {
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return false;
      ctx = new AC();
    }
    if (ctx.state === "suspended") {
      ctx.resume();
    }
    unlocked = true;
    return true;
  } catch {
    return false;
  }
}

export function isChimeUnlocked() {
  return unlocked;
}

// Play the chime. Safe to call anytime — silently no-ops if audio is not
// yet unlocked by a user gesture.
export function playChime(volume = 0.35) {
  if (!ctx || ctx.state !== "running") return false;
  try {
    const now = ctx.currentTime;
    // Two overlapping bell tones for a warm "ding" character.
    const tones = [
      { f: 880, start: 0.0, dur: 0.55, gain: volume },      // A5
      { f: 1318.5, start: 0.02, dur: 0.5, gain: volume * 0.7 }, // E6
      { f: 659.25, start: 0.0, dur: 0.65, gain: volume * 0.5 }, // E5 body
    ];
    for (const t of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(t.f, now + t.start);
      gain.gain.setValueAtTime(0.0001, now + t.start);
      // Quick attack, exponential decay — feels like a struck bell.
      gain.gain.exponentialRampToValueAtTime(t.gain, now + t.start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t.start + t.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t.start);
      osc.stop(now + t.start + t.dur + 0.05);
    }
    return true;
  } catch {
    return false;
  }
}
